import * as React from 'react';
import styled from 'styled-components';
import { useTrackingService } from '@/contexts/TrackingServiceContext';
import { TrackingEvent } from '@/util/tracking/definitions';
import { ClusterButton } from './ClusterButton';
import { CLUSTER_DATA } from './ClusterButton.types';
import { CLUSTER_BUTTON_TYPES } from '@/enums/ClusterButton';

interface ClusteredButtonContainerProps {
  tabs: CLUSTER_DATA[];
  activeTab: CLUSTER_BUTTON_TYPES;
  onTabChange: (label: CLUSTER_BUTTON_TYPES) => void;
  backgroundColor?: string;
  className?: string;
}

export const CTAClusterContainer = styled.div<{ $backgroundColor?: string }>`
  display: flex;
  gap: ${({ theme }) => theme.size.static150};
  pointer-events: all;
  width: fit-content;
  background-color: ${({ $backgroundColor }) => $backgroundColor ?? 'white'};
  border-radius: ${({ theme }) => theme.size.static150};
  padding: ${({ theme }) => `${theme.size.static100} ${theme.size.static150}`};
`;

export const ClusteredButtonContainer = ({
  tabs,
  activeTab,
  onTabChange,
  backgroundColor,
  className,
}: ClusteredButtonContainerProps): React.JSX.Element => {
  const trackingService = useTrackingService();

  const handleTabClick = React.useCallback(
    (label: CLUSTER_BUTTON_TYPES, ariaLabel: string) => {
      onTabChange(label);

      trackingService.track(TrackingEvent.VWBasicButtonClick, {
        link: {
          url: ariaLabel,
          name: label,
        },
        eventInfo: {
          contentId: `clicked button: ${ariaLabel}-${label}`,
        },
      });
    },
    [onTabChange, trackingService],
  );

  return (
    <CTAClusterContainer
      className={className}
      $backgroundColor={backgroundColor}
      data-testid="cluster-button-container"
    >
      {tabs.map((tab: CLUSTER_DATA) => (
        <ClusterButton
          data-testid={`cluster-button-${tab.name}`}
          key={tab.name}
          ariaLabel={tab.ariaLabel}
          active={activeTab === tab.name}
          handleClick={() => handleTabClick(tab.name, tab.ariaLabel)}
          icon={tab.icon}
        />
      ))}
    </CTAClusterContainer>
  );
};
